// Хүлээлцэл tab — loads the loading clerk finished (pull sheet CONFIRMED,
// «Ачиж дууссан») that the warehouse keeper still has to accept. Accepting
// (PUT /pull_sheet/:id/confirm) flips the sheet to LOADED and auto-creates the
// warehouse -> driver handovers listed below this card. Until now the accept
// step existed only in the mobile app, so a keeper on the web never saw these.

import { Badge, Box, Button, Group, Table, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { CheckIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import useSWR from "swr"
import * as PullSheetApi from "../../apis/pullsheet"
import { IAuth } from "../../store/Auth"
import { message } from "../../utils/message"
import { CodeStack, TableFrame, TablePager } from "../delivery/atoms"
import { translateBackendError } from "../delivery/translate-error"
import { RouteNameCell } from "../delivery-routes/RouteTable"
import { usePermission } from "../permission"
import { ListState } from "../ui"
import RenderText from "../ui/render-text"
import classes from "./custody.module.css"

type LoadedSheet = {
  id: string
  refCode?: string | null
  warehouseStaffId?: string | null
  endedDate?: string | null
  deliveryNoteCount?: number
  totalQuantity?: number
  hasShortage?: boolean
  warehouse?: { name?: string | null } | null
  staff?: { firstName?: string | null; lastName?: string | null } | null
  routes?: Array<{ routeNumber?: string | null; name?: string | null }>
}

function personName(p: LoadedSheet["staff"]): string {
  if (!p) return "-"
  return [p.lastName?.charAt(0) ? `${p.lastName.charAt(0)}.` : null, p.firstName].filter(Boolean).join(" ") || "-"
}

/** CONFIRMED pull sheets the keeper still has to accept; `enabled` is false without a list permission. */
export function useLoadConfirmQueue() {
  // Each hook runs on every render (no short-circuit), so the hook order stays fixed.
  const psList = usePermission("ORD_PS_LIST").hasAccess
  const psDetail = usePermission("ORD_PS_DETAIL").hasAccess
  const enabled = psList || psDetail
  const { data, mutate, isLoading } = useSWR<{ rows: LoadedSheet[]; count: number }>(
    enabled ? ["pull-sheets.awaiting-confirm"] : null,
    () =>
      PullSheetApi.list({
        filter: { pullSheetStatus: "CONFIRMED" },
        offset: { page: 1, limit: 50 },
        order: ["createdAt", "ASC"],
      } as never),
    { revalidateOnFocus: true, refreshInterval: 60_000 },
  )
  return { enabled, rows: data?.rows ?? [], isLoading, mutate }
}

export function LoadConfirmQueue({
  rows,
  isLoading,
  onConfirmed,
}: Readonly<{ rows: LoadedSheet[]; isLoading: boolean; onConfirmed: () => Promise<unknown> | void }>) {
  const { t } = useTranslation("delivery")
  const userId = useSelector((s: { auth: IAuth }) => s.auth.user?.id ?? null)
  const psMng = usePermission("ORD_PS_MNG").hasAccess
  const psAssign = usePermission("ORD_DN_PULLSHEET_ASSIGN").hasAccess
  const canManage = psMng || psAssign
  const [busyId, setBusyId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  if (isLoading) return <ListState state="loading" columns={6} />
  if (rows.length === 0) {
    return <ListState state="empty" title={t("manage.handover.loadQueueEmpty")} description={t("manage.handover.loadQueueText")} />
  }
  const safePage = Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize)))
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize)

  const confirmSheet = async (ps: LoadedSheet) => {
    setBusyId(ps.id)
    try {
      await PullSheetApi.confirm(ps.id)
      message.success(t("manage.handover.confirmLoadSuccess"))
      await onConfirmed()
    } catch (e) {
      message.error(translateBackendError(e))
    } finally {
      setBusyId(null)
    }
  }

  const askConfirm = (ps: LoadedSheet) =>
    modals.openConfirmModal({
      title: t("manage.handover.confirmLoadTitle"),
      children: (
        <Text fz="var(--nw-fz-body)">{t("manage.handover.confirmLoadText", { code: ps.refCode ?? "" })}</Text>
      ),
      labels: { confirm: t("manage.handover.confirmLoad"), cancel: t("manage.handover.cancel") },
      onConfirm: () => confirmSheet(ps),
    })

  return (
    <Box>
      <TableFrame
        footer={<TablePager page={safePage} pageSize={pageSize} total={rows.length} onPageChange={setPage} onPageSizeChange={setPageSize} />}
      >
        <Table.ScrollContainer minWidth={880}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={160}>{t("manage.col.action")}</Table.Th>
                <Table.Th>{t("manage.handover.colPullSheet")}</Table.Th>
                <Table.Th>{t("manage.handover.colRoute")}</Table.Th>
                <Table.Th>{t("manage.handover.colLoader")}</Table.Th>
                <Table.Th>{t("manage.handover.colLoad")}</Table.Th>
                <Table.Th>{t("manage.handover.colEnded")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((ps) => {
                const allowed = canManage || (!!userId && ps.warehouseStaffId === userId)
                const route = ps.routes?.[0]
                return (
                  <Table.Tr key={ps.id} className={classes.row}>
                    <Table.Td>
                      <Button
                        size="xs"
                        color="dark"
                        leftSection={<CheckIcon size={14} />}
                        disabled={!allowed}
                        loading={busyId === ps.id}
                        onClick={() => askConfirm(ps)}
                      >
                        {t("manage.handover.confirmLoad")}
                      </Button>
                    </Table.Td>
                    <Table.Td>
                      <CodeStack primary={ps.refCode ?? "-"} secondary={ps.warehouse?.name ?? undefined} />
                    </Table.Td>
                    <Table.Td>
                      <RouteNameCell name={route?.name} routeNumber={route?.routeNumber} />
                    </Table.Td>
                    <Table.Td>
                      <RenderText fz="var(--nw-fz-body)" text={personName(ps.staff)} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <RenderText
                          fz="var(--nw-fz-body)"
                          text={t("manage.handover.loadSummary", { dn: ps.deliveryNoteCount ?? 0, qty: ps.totalQuantity ?? 0 })}
                        />
                        {ps.hasShortage && (
                          <Badge size="sm" color="orange" variant="light">
                            {t("manage.handover.shortage")}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <RenderText
                        ff="monospace"
                        fz="var(--nw-fz-mono)"
                        text={ps.endedDate ? dayjs(ps.endedDate).format("MM/DD HH:mm") : null}
                      />
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </TableFrame>
    </Box>
  )
}
